import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Building2, Globe, Star, Award, Printer,
} from "lucide-react";

/**
 * Prime Supplier Matchmaking Brief — public, no-login page for
 * Travel + Leisure management to review every registered Prime's
 * profile + matchmaking interests. Used to invite the right SBEs
 * for business matchmaking.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
};

export default function MatchmakingBrief() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/matchmaking/brief/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer valid."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <PageShell>
        <div className="text-sm uppercase tracking-[0.18em] text-zinc-500">
          Loading Prime Supplier brief…
        </div>
      </PageShell>
    );
  }
  if (error) {
    return (
      <PageShell>
        <div className="max-w-lg">
          <div className="overline">Matchmaking Brief</div>
          <h1 className="serif text-3xl mt-3">Link no longer valid</h1>
          <p className="text-sm text-zinc-600 mt-4">{error}</p>
          <p className="text-xs text-zinc-500 mt-4">
            Reach out to Display America at{" "}
            <a href="mailto:info@displayamerica.com" className="underline underline-offset-4">
              info@displayamerica.com
            </a>{" "}
            for a fresh link.
          </p>
        </div>
      </PageShell>
    );
  }

  const primes = data.primes || [];

  return (
    <PageShell>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="overline">Prime Supplier Matchmaking Brief · Display America</div>
          <h1 className="serif text-3xl md:text-4xl mt-2" data-testid="brief-title">
            Prime Supplier profiles &amp; matchmaking interests
          </h1>
          <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
            A live directory for Travel + Leisure sourcing leadership. Use it to
            identify Small Business Enterprises whose capabilities align with
            each Prime&apos;s matchmaking interests, then invite those SBEs to
            Business Matchmaking Opportunities on Sept 11, 2026.
          </p>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 mt-4">
            Generated {fmtDate(data.generated_at)} · {primes.length} Prime{primes.length === 1 ? "" : "s"} registered
          </div>
        </div>
        <button onClick={() => window.print()}
                className="btn-outline-black h-10 px-4 text-xs uppercase tracking-[0.18em] no-print inline-flex items-center gap-2"
                data-testid="brief-print">
          <Printer size={14} /> Print
        </button>
      </div>

      {primes.length === 0 ? (
        <div className="border border-zinc-200 p-6 text-sm text-zinc-500">
          No registered Prime Suppliers yet. Check back after registrations open.
        </div>
      ) : (
        <div className="grid gap-6">
          {primes.map((p) => <PrimeCard key={p.id} p={p} />)}
        </div>
      )}

      <footer className="mt-16 pt-6 border-t border-zinc-200 text-xs text-zinc-500 no-print">
        Confidential — for Travel + Leisure sourcing leadership. Do not forward.
        Questions? Write to{" "}
        <a href="mailto:info@displayamerica.com" className="underline underline-offset-4">
          info@displayamerica.com
        </a>. Display America, on behalf of Travel + Leisure.
      </footer>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .prime-card { break-inside: avoid; page-break-inside: avoid; }
          body { background: #fff !important; }
        }
      `}</style>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-white text-black">
      <header className="border-b border-zinc-200 no-print">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="serif text-lg leading-none">TRAVEL + LEISURE</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-0.5">
              Strategic Supplier Summit 2026
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 hidden sm:block">
            Matchmaking Brief
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10 md:py-14">{children}</main>
    </div>
  );
}

function PrimeCard({ p }) {
  const logoHref = p.logo_file_id ? `${BACKEND_URL}/api/files/${p.logo_file_id}` : null;
  return (
    <article className="prime-card border border-zinc-200 p-6 md:p-8" data-testid={`brief-prime-${p.id}`}>
      <div className="flex items-start gap-5">
        {logoHref ? (
          <img src={logoHref} alt=""
               className="h-16 w-16 object-contain border border-zinc-200 bg-white shrink-0" />
        ) : (
          <div className="h-16 w-16 border border-zinc-200 flex items-center justify-center text-zinc-300 shrink-0">
            <Building2 size={22} strokeWidth={1.2} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="serif text-2xl leading-tight">{p.company_name}</h2>
            {p.booth_number && (
              <span className="text-[10px] uppercase tracking-[0.18em] border border-[#A39171] text-[#A39171] bg-[#A39171]/10 px-2 py-1 shrink-0">
                Station {p.booth_number}
              </span>
            )}
          </div>
          {(p.industry || p.industry_category) && (
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">
              {[p.industry, p.industry_category].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      </div>

      {p.description && (
        <p className="text-sm text-zinc-700 mt-5 leading-relaxed">{p.description}</p>
      )}

      {p.products_services && (
        <div className="mt-4 border-l-2 border-zinc-200 pl-4 text-sm text-zinc-700">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Products &amp; Services</div>
          {p.products_services}
        </div>
      )}

      {p.matchmaking_preferences?.length > 0 && (
        <div className="mt-5 bg-[#A39171]/5 border border-[#A39171]/30 p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#A39171] flex items-center gap-1.5">
            <Star size={11} className="fill-current" /> Matchmaking Interests · SBE categories they want to meet
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {p.matchmaking_preferences.map((mp, i) => (
              <span key={i} className="text-xs bg-white border border-[#A39171]/40 px-2 py-1">
                {mp}
              </span>
            ))}
          </div>
        </div>
      )}

      {p.business_areas?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Business Areas</div>
          <div className="flex flex-wrap gap-1.5">
            {p.business_areas.map((b, i) => (
              <span key={i} className="text-xs bg-zinc-100 px-2 py-1">{b}</span>
            ))}
          </div>
        </div>
      )}

      {p.certifications?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
            <Award size={11} /> Certifications
          </div>
          <div className="flex flex-wrap gap-1.5">
            {p.certifications.map((c, i) => (
              <span key={i} className="text-xs bg-zinc-100 px-2 py-1">{c}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-zinc-100 grid sm:grid-cols-2 gap-y-1.5 gap-x-6 text-xs text-zinc-600">
        {p.website && (
          <div className="min-w-0">
            <span className="uppercase tracking-[0.18em] text-[10px] text-zinc-400 flex items-center gap-1"><Globe size={9} /> Website</span>
            <a href={p.website} target="_blank" rel="noreferrer"
               className="underline underline-offset-4 truncate block hover:text-black">
              {p.website.replace(/^https?:\/\//, "")}
            </a>
          </div>
        )}
      </div>
    </article>
  );
}
